import { Injectable, inject } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { debounceTime, map } from "rxjs/operators";
import { Anskaffningsformer, Verksamhetshandelse, VerksamhetshandelseTyp } from "~app/common/models/constants";
import { VolymAttFordelaDTO } from "~app/common/models/planera/v2/VolymAttFordelaDTO";
import { ApiClientService } from "~app/common/services/api-client.service";
import { IArbetsorderDetail } from "~app/features/planera/model/IArbetsorderDetail";
import { IArbetsorderHandelssortimentsgrupp } from "~app/features/planera/model/IArbetsorderHandelssortimentsgrupp";
import { IVerksamhetshandelser } from "~app/features/planera/model/IVerksamhetshandelser";
import { IHandelssortiment, IProduktvalRow } from "~app/features/planera/produktval/produktval.models";
import { ArbetsorderWrapper } from "../planera-detail/planera-detail-models";
import { IProduktionsmall, Produktinstruktion, ProduktvalRow } from "./arbetsorder-produktval.models";

@Injectable({
    providedIn: "root"
})
export class ArbetsorderProduktvalService {
    produktionsMallar$: BehaviorSubject<IProduktionsmall[]>;
    senastUtbytessimulerad$: Subject<IVerksamhetshandelser>;
    senastVolymhamtad$: Subject<IVerksamhetshandelser>;
    volymAttFordela$: BehaviorSubject<VolymAttFordelaDTO[]>;

    private readonly apiClient = inject(ApiClientService);

    constructor() {
        this.produktionsMallar$ = new BehaviorSubject<IProduktionsmall[]>([]);
        this.volymAttFordela$ = new BehaviorSubject<VolymAttFordelaDTO[]>([]);
        this.senastUtbytessimulerad$ = new Subject<IVerksamhetshandelser>();
        this.senastVolymhamtad$ = new Subject<IVerksamhetshandelser>();
    }

    checkSetProduktval(arbetsorder: IArbetsorderDetail): Observable<boolean> {
        const isApplicable = arbetsorder && (arbetsorder.isLeveransGrot || arbetsorder.isGrotskotning);
        if (!isApplicable) {
            return new Observable<boolean>(v => {
                v.next(false);
                v.complete();
            });
        }

        return this.apiClient.post(`Arbetsorder/${arbetsorder.id}/setproduktval`, {}).pipe(map(() => true));
    }

    getHandelssortimentsgrupper(arbetsorderId: number, periodFrom: string, periodTom: string): Observable<IArbetsorderHandelssortimentsgrupp[]> {
        const params = {
            arbetsorderId: arbetsorderId,
            periodFrom: periodFrom,
            periodTom: periodTom
        };
        return this.apiClient.get<IArbetsorderHandelssortimentsgrupp[]>(`Planera/GetHandelssortiment`, true, params);
    }

    isProduktvalApplicable(arbetsorder: IArbetsorderDetail): boolean {
        return !this.isArbetsorderLeveransgrot(arbetsorder) && !this.isArbetsorderGrotskotning(arbetsorder);
    }

    isSimuleringApplicable(arbetsorder: IArbetsorderDetail): boolean {
        return !Anskaffningsformer.isVirkesKop(arbetsorder?.anskaffningsform) && !this.isArbetsorderLeveransgrot(arbetsorder) && !this.isArbetsorderGrotskotning(arbetsorder);
    }

    produktvalRowFromHandelssortiment(arbetsorderWrapper: ArbetsorderWrapper, hs: IHandelssortiment): IProduktvalRow {
        const row = new ProduktvalRow(arbetsorderWrapper);
        row.namn = hs.namn;
        row.produktinstruktioner = [];
        row.handelssortimentId = hs.handelssortimentId;
        row.handelssortimentsgrupp = hs.handelssortimentsgruppNamn;
        row.hasProduktionsmal = hs.hasProduktionsmal;
        return row;
    }

    produktvalRowsFromArbetsorder(arbetsorderWrapper: ArbetsorderWrapper): IProduktvalRow[] {
        const result: IProduktvalRow[] = [];
        arbetsorderWrapper.arbetsorder.handelssortiment.forEach(hs => {
            if (hs.produkter.length > 0) {
                hs.produkter.forEach(prod => {
                    const row = new ProduktvalRow(arbetsorderWrapper);
                    row.produktinstruktioner = prod.produktinstruktioner.map(g => new Produktinstruktion(g.namn, g.produktinstruktionId, g.puid));
                    row.produkt = prod.namn;
                    row.handelssortimentId = hs.handelssortimentId;
                    row.produktId = prod.produktId;
                    row.justeradKvantitet = prod.justeradKvantitet;
                    const utbytessimulerad = prod.produktinstruktioner.filter(g => g.medIUtbytessimulering)[0];
                    row.medTillUtbytessimulering = utbytessimulerad && utbytessimulerad.produktinstruktionId;
                    row.simuleradKvantitet = prod.kvantitet;
                    row.namn = hs.namn;
                    row.handelssortimentsgrupp = hs.handelssortimentsgruppNamn;
                    row.handelssortimentKategori = prod.handelssortimentKategori;
                    row.simuleradKvantitet = prod.kvantitet;

                    result.push(row);
                });
            } else {
                result.push(this.produktvalRowFromHandelssortiment(arbetsorderWrapper, hs));
            }

        });
        return result;
    }

    refreshSenastSimulerad(arbetsorderguid: string): void {
        this.refreshVerksamhetshandelse(VerksamhetshandelseTyp.Arbetsorder, Verksamhetshandelse.Utbytessimulerad, arbetsorderguid)
            .pipe(debounceTime(1000))
            .subscribe(x => {
                this.senastUtbytessimulerad$.next(x);
            });
    }

    refreshVerksamhetshandelse(klass: string, handelse: string, guid: string): Observable<IVerksamhetshandelser> {
        const params = {
            klass: klass,
            handelse: handelse,
            guid: guid
        };
        return this.apiClient.get<IVerksamhetshandelser>("Verksamhetshandelser/GetLatestVerksamhetshandelse", true, params);
    }

    refreshVolymerAttFordela(id: number): void {
        this.apiClient.get<VolymAttFordelaDTO[]>(`Planera/GetVolymAttFordela`, false, { arbetsorderId: id })
            .subscribe(x => {
                this.volymAttFordela$.next(x);
            });
    }

    refreshVolymerHamtade(arbetsorderguid: string): void {
        this.refreshVerksamhetshandelse(VerksamhetshandelseTyp.Arbetsorder, Verksamhetshandelse.KopieraHandelssortimentFromKontrakt, arbetsorderguid)
            .subscribe(x => {
                this.senastVolymhamtad$.next(x);
            });
    }

    refreshproduktionsMallar(): void {
        this.apiClient.get<IProduktionsmall[]>("mina/produktionsmallar").subscribe(x => {
            this.produktionsMallar$.next(x);
        });
    }

    utbytessimuleraFromVirkeskopskontrakt(id: number): Observable<any> {
        return this.apiClient.post(`Arbetsorder/${id}/UtbytessimuleraFromVirkeskopskontrakt`, {}, false);
    }

    utbytessimuleraFromVirkeskopskontraktWithProduktmall(arbetsorderid: number, produktionsmallid: number): Observable<any> {
        return this.apiClient.post(`Arbetsorder/${arbetsorderid}/UtbytessimuleraFromVirkeskopskontraktWithProduktmall`, {}, false, {
            produktionsmallid: produktionsmallid
        });
    }

    private isArbetsorderGrotskotning(arbetsorder: IArbetsorderDetail): boolean {
        return arbetsorder && arbetsorder.isGrotskotning;
    }

    private isArbetsorderLeveransgrot(arbetsorder: IArbetsorderDetail): boolean {
        return arbetsorder && arbetsorder.isLeveransGrot;
    }
}
